import { useTranslation } from "react-i18next";
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Truck, Store } from "lucide-react";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { useLocation } from "wouter";

interface OrderConfirmationProps {
  orderId?: string;
  orderNumber?: string;
  /** P/O reference the user entered for the order. */
  poNumber?: string;
  /** Eclipse sales-order id returned by the Hajoca submission. */
  hajocaOrderId?: string;
  /** Eclipse order confirmation reference, when returned. */
  orderConfirmation?: string;
  fulfillmentType?: "delivery" | "pickup";
  itemCount: number;
  totalCost?: number;
  onStartNewOrder: () => void;
  onSaveAsTemplate?: () => void;
}

export function OrderConfirmation({
  orderId: _orderId,
  orderNumber,
  poNumber,
  hajocaOrderId,
  orderConfirmation,
  fulfillmentType,
  itemCount,
  totalCost,
  onStartNewOrder,
  onSaveAsTemplate,
}: OrderConfirmationProps) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();

  return (
    <div className="flex justify-center py-12">
      <div className="w-full max-w-[520px] space-y-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <CheckCircle2 className="h-12 w-12 text-green-500" aria-hidden="true" />
          <h2 className="text-2xl font-bold" data-step-heading tabIndex={-1}>{t("components.orders.orderConfirmation.orderSubmitted")}</h2>
          <p className="text-muted-foreground">
            {t("components.orders.orderConfirmation.yourOrderHasBeen")}
          </p>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-3 text-sm">
            {hajocaOrderId && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("components.orders.orderConfirmation.hajocaOrderNumber")}</span>
                <span className="font-medium">{hajocaOrderId}</span>
              </div>
            )}
            {orderConfirmation && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("components.orders.orderConfirmation.confirmationNumber")}</span>
                <span className="font-medium">{orderConfirmation}</span>
              </div>
            )}
            {poNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("components.orders.orderConfirmation.poNumber")}</span>
                <span className="font-medium">{poNumber}</span>
              </div>
            )}
            {orderNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t("components.orders.orderConfirmation.orderNumber")}</span>
                <span className="font-medium">{orderNumber}</span>
              </div>
            )}
            {fulfillmentType && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t("components.orders.orderConfirmation.fulfillment")}</span>
                <span className="flex items-center gap-1.5 font-medium">
                  {fulfillmentType === "delivery" ? (
                    <Truck className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Store className="h-4 w-4" aria-hidden="true" />
                  )}
                  {fulfillmentType === "delivery" ? t("components.orders.orderConfirmation.delivery") : t("components.orders.orderConfirmation.pickup")}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t("components.orders.orderConfirmation.items")}</span>
              <span className="font-medium">{itemCount}</span>
            </div>
            {totalCost != null && totalCost > 0 && (
              <>
                <div className="flex justify-between pt-2 border-t">
                  <span className="text-muted-foreground">{t("components.orders.orderConfirmation.total")}</span>
                  <span className="font-semibold text-base">
                    ${formatCentsAsDollars(totalCost)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground text-right">
                  {t("components.orders.orderConfirmation.estimateExcludesTax")}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-center">
          <Button variant="outline" onClick={() => navigate("/orders")}>
            {t("components.orders.orderConfirmation.viewAllOrders")}
          </Button>
          <Button onClick={onStartNewOrder}>{t("components.orders.orderConfirmation.startNewOrder")}</Button>
        </div>

        {onSaveAsTemplate && (
          <button
            onClick={onSaveAsTemplate}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("components.orders.orderConfirmation.saveAsTemplateFor")}
          </button>
        )}
      </div>
    </div>
  );
}
